import { useRef } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { useChatInputStore } from '@/store/chatInput';
import { useArtifactViewerStore } from '@/store/artifactViewer';
import { useChatStore } from '@/store/chat';
import { useCoworkerStore } from '@/store/coworker';
import { isWorkModeDisabled, workModeUnavailableMessage } from '@/lib/workModeAvailability';
import { hasComposerDraftContent } from '@/lib/composerSendDraft';

// Artifact studios also open outside conversations, such as the Files library.
// Resolve the attachment first; only create a draft when it is ready to hand off.
export default function useArtifactEditConversation() {
  const pathname = usePathname();
  const router = useRouter();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const captureTarget = () => ({ pathname: pathnameRef.current, contextId: useChatInputStore.getState().currentContextId });
  const assertTarget = target => {
    if (pathnameRef.current !== target.pathname || useChatInputStore.getState().currentContextId !== target.contextId) {
      throw new Error('The active chat changed. Please try again in the intended chat.');
    }
  };
  const addRequest = (target, { file, prompt, mode }) => {
    assertTarget(target);
    const work = mode === 'coworker';
    if (work && isWorkModeDisabled()) throw new Error(workModeUnavailableMessage());
    const chat = useChatStore.getState().chats.find(chat => chat.id === target.contextId);
    const inWork = !!(chat?.flags?.isCoworkerSession || chat?.coworkerSession)
      || (target.contextId === 'new' && useCoworkerStore.getState().composerMode === 'coworker');
    // A conversation's send handler owns its execution mode. Changing the global
    // composer toggle cannot turn a generator chat into a Work session.
    const startNew = !target.contextId || /^\/files(?:\/|$)/.test(target.pathname || '') || (work && !inWork);
    if (work && startNew && !file) throw new Error('This video is still being saved. Please try again shortly.');
    if (work && startNew && hasComposerDraftContent(useChatInputStore.getState().inputStates.new)) {
      throw new Error('You have an unsent new-chat draft. Send or clear it before starting a video edit in Work.');
    }
    if (work) useChatInputStore.setState({ pendingAutoSend: null });
    if (startNew) useChatInputStore.getState().initializeNewChatWithFiles([]);
    const composer = useChatInputStore.getState();
    const contextId = composer.currentContextId;
    const existing = composer.inputStates[contextId]?.text?.trim();
    if (file) composer.addFile(file);
    composer.setText(existing ? `${existing}\n\n${prompt}` : prompt);
    composer.requestInputFocus(contextId);
    if (work) useCoworkerStore.getState().setComposerMode('coworker');
    if (startNew) {
      useArtifactViewerStore.getState().closeArtifactViewer();
      router.push('/app');
    }
    return contextId;
  };

  return { captureTarget, assertTarget, addRequest };
}
